import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Spinner,
  useToast,
  Flex,
  Avatar,
  Card,
  CardBody,
  Divider,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { ArrowUpIcon, AddIcon, ChatIcon } from '@chakra-ui/icons';
import ReactMarkdown from 'react-markdown';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';

const DataAgentChat = ({ contextType, height = '500px', collapsedHeight, minHeight, maxHeight }) => {
  const { accessToken, user } = useContext(AppContext);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const toast = useToast();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (contextType && accessToken) {
      fetchSessions();
    }
  }, [contextType, accessToken]);

  const fetchSessions = async () => {
    setIsLoadingSessions(true);
    try {
      // We reuse the existing chat sessions endpoint, passing 'data-agent' as the case_id
      const response = await callApi(`/api/v2/chat/sessions?case_id=data-agent&context_type=${contextType}`, accessToken);
      const data = await response.json();
      if (response.ok) {
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error("Error fetching chat sessions:", error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const loadChatHistory = async (id) => {
    setIsLoading(true);
    try {
      const response = await callApi(`/api/v2/chat/history/${id}`, accessToken);
      const data = await response.json();
      if (response.ok) {
        setChatId(id);
        setMessages(data.messages || []);
      } else {
        throw new Error(data.error || 'Failed to load chat history');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startNewChat = () => {
    setChatId(null);
    setMessages([]);
    setInputValue('');
  };

  const defaultCollapsedHeight = '150px';
  const currentHeight = messages.length === 0 ? (collapsedHeight || defaultCollapsedHeight) : height;

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      actor: 'user',
      message: inputValue,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const payload = {
        question: userMessage.message,
        context_type: contextType,
        chat_id: chatId
      };

      // Point to the new Data Agent endpoint
      const response = await callApi('/api/v2/data_agent/query', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        const isNewChat = !chatId;
        setChatId(data.chat_id);
        setMessages(data.history);
        
        if (isNewChat) {
          fetchSessions();
        }
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Card 
      height={currentHeight} 
      minHeight={minHeight} 
      maxHeight={maxHeight} 
      variant="outline" 
      borderColor="gray.200"
      overflow="hidden"
      transition="height 0.3s ease-in-out"
    >
      <CardBody display="flex" flexDirection="row" p={0} height="100%">
        
        {/* Sidebar: Chat History */}
        <Box 
          w="250px" 
          borderRightWidth="1px" 
          borderColor="gray.200" 
          bg="white" 
          display="flex" 
          flexDirection="column"
        >
          <Box p={3} borderBottomWidth="1px" borderColor="gray.100">
            <Button 
              w="100%" 
              size="sm" 
              leftIcon={<AddIcon />} 
              colorScheme="blue" 
              variant="outline"
              onClick={startNewChat}
            >
              New Chat
            </Button>
          </Box>
          <Box flex="1" overflowY="auto" p={2}>
            {isLoadingSessions ? (
              <Flex justify="center" p={4}><Spinner size="sm" color="gray.400" /></Flex>
            ) : sessions.length === 0 ? (
              <Text fontSize="xs" color="gray.400" textAlign="center" mt={4}>No previous chats</Text>
            ) : (
              <VStack align="stretch" spacing={1}>
                {sessions.map(session => (
                  <Box 
                    key={session.id}
                    p={2} 
                    rounded="md" 
                    cursor="pointer"
                    bg={chatId === session.id ? "blue.50" : "transparent"}
                    _hover={{ bg: chatId === session.id ? "blue.50" : "gray.50" }}
                    onClick={() => loadChatHistory(session.id)}
                  >
                    <HStack spacing={2}>
                      <ChatIcon color={chatId === session.id ? "blue.500" : "gray.400"} boxSize={3} />
                      <VStack align="start" spacing={0} flex="1" overflow="hidden">
                        <Text fontSize="xs" fontWeight={chatId === session.id ? "bold" : "medium"} color="gray.700" isTruncated w="100%">
                          {session.preview}
                        </Text>
                        <Text fontSize="2xs" color="gray.400">
                          {new Date(session.updated_at).toLocaleDateString()}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </Box>
        </Box>

        {/* Main Chat Area */}
        <Box flex="1" display="flex" flexDirection="column" minW="0">
          <Box flex="1" overflowY="auto" p={4} bg="gray.50" display="flex" flexDirection="column">
            <VStack spacing={4} align="stretch" flex="1" justify={messages.length === 0 ? "center" : "flex-start"}>
              {messages.length === 0 && (
                <Box textAlign="center" color="gray.500">
                  <Text fontSize="sm" fontWeight="medium">Ask a question about this data.</Text>
                  <Text fontSize="xs" mt={1}>The AI will query the database to find the answer.</Text>
                </Box>
              )}
              {messages.map((msg, index) => (
                <Flex
                  key={index}
                  justify={msg.actor === 'user' ? 'flex-end' : 'flex-start'}
                >
                  <HStack
                    align="start"
                    spacing={2}
                    maxW="80%"
                    flexDirection={msg.actor === 'user' ? 'row-reverse' : 'row'}
                  >
                    <Avatar
                      size="xs"
                      name={msg.actor === 'user' ? 'User' : 'AI'}
                      bg={msg.actor === 'user' ? 'blue.500' : 'purple.500'}
                      color="white"
                    />
                    <Box
                      bg={msg.actor === 'user' ? 'blue.500' : 'white'}
                      color={msg.actor === 'user' ? 'white' : 'black'}
                      p={3}
                      borderRadius="lg"
                      boxShadow="sm"
                      borderWidth={msg.actor === 'user' ? 0 : '1px'}
                    >
                      <Box className="markdown-content" fontSize="sm">
                        <ReactMarkdown>{msg.message}</ReactMarkdown>
                      </Box>
                    </Box>
                  </HStack>
                </Flex>
              ))}
              {isLoading && (
                <Flex justify="flex-start">
                  <HStack align="center" spacing={2}>
                    <Avatar size="xs" bg="purple.500" icon={<Spinner size="xs" />} />
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                      Querying database...
                    </Text>
                  </HStack>
                </Flex>
              )}
              <div ref={messagesEndRef} />
            </VStack>
          </Box>

          {/* Input Area */}
          <Box p={4} bg="white" borderTopWidth="1px">
            <HStack>
              <Input
                placeholder="e.g., Which payer has the most leakage?"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                bg="gray.50"
              />
              <Button
                colorScheme="blue"
                onClick={handleSendMessage}
                isLoading={isLoading}
                isDisabled={!inputValue.trim()}
                leftIcon={<ArrowUpIcon />}
              >
                Send
              </Button>
            </HStack>
          </Box>
        </Box>
      </CardBody>
    </Card>
  );
};

export default DataAgentChat;
